<template>
  <div>
    <button
      v-if="facetFilterProperties && Object.keys(facetFilterProperties).length"
      class="button button--medium button--primary facet-filter-opener"
      v-text="translate('btnFilterShow', { count: this.resultList.length })"
      @click="facetFilterOpenOnMobile = true"
    ></button>

    <div class="result-view">

      <div class="varieties-menu"
         v-if="isFacetFilterDisplayable || isSortOptionsDisplayable">
        <facet-filter
          v-if="isFacetFilterDisplayable"
          :filter-properties="facetFilterProperties"
          :open="facetFilterOpenOnMobile"
          :default-settings="defaultSettings"
          :current-settings="userInput"
          :filter-settings-key="filterSettingsKey"
          :crop="crop"
          @close="facetFilterOpenOnMobile = false"
          @change="filterResults"
          @reset-sort-order="resetSortOrder"
        ></facet-filter>
        <sort-order
          v-if="isSortOptionsDisplayable"
          :sort-options="sortOptions"
          :preset-order="userInput.sortId"
          :mandatory="sortOrderMandatory"
          :filter-settings-key="filterSettingsKey"
          @change="reorderResults"
        ></sort-order>
        <div v-if="benchmarkingToolLink && Object.keys(benchmarkingToolLink).length" class="benchmarking-tool-link">
          <p v-text="benchmarkingToolLink.text"></p>
          <a :href="benchmarkingToolLink.url">
            <img :srcset="benchmarkingToolLink.srcset" sizes="(min-width: 1240px) 32rem, 20rem" :alt="benchmarkingToolLink.alt">
          </a>
        </div>
      </div>

      <div class="results">
      <div class="varieties">
        <result-view-item
          v-for="variety in slicedResultList" :key="variety.id"
          :variety="variety"
          :crop="crop"
          :activate-comparison="activateComparison"
          :activate-booking-link="activateBookingLink"
          :activate-salesforce-form-link="activateSalesforceFormLink"
          :activate-subheadline="activateSubheadline"
          :too-much-varieties-selected="!comparisonPossible"
          :booking-link="bookingLink"
          :salesforce-form-link="salesforceFormLink"
          :language="language"
          :comparison-selection-label="comparisonSelectionLabel"
          @toggle-comparison="toggleComparison"
        ></result-view-item>
          <p v-if="preLoaded && !resultList.length" v-text="translate('lblResultListEmpty')"></p>
        </div>
        <div class="load-more">
          <button
            v-if="moreResultsAvailable && limitNumberOfResults"
            class="button button--medium button--primary"
            aria-expanded="false"
            v-text="translate('lblShowMoreResults')"
            @click="showMoreResults()"
          ></button>
        </div>
        <div v-if="benchmarkingToolLink" class="benchmarking-tool-link__mobile">
          <p v-text="benchmarkingToolLink.text"></p>
          <a :href="benchmarkingToolLink.url">
            <img :srcset="benchmarkingToolLink.srcset" sizes="(min-width: 694px) 45rem, (min-width: 566px) 40rem, (min-width: 374px) 32rem, 20rem" :alt="benchmarkingToolLink.alt">
          </a>
        </div>
      </div>
    </div>

    <compare-button
      v-if="activateComparison"
      :crop="crop"
      :max-nr-of-varieties-to-be-compared="maxNrOfVarietiesToBeCompared"
      :hint-visible-duration="hintVisibleDuration"
      :ids-to-be-compared="idsToBeCompared"
      :comparison-view="comparisonViewLink"
      :open-comparison-label="openComparisonLabel"
    ></compare-button>

    <notification-modal
      v-if="error"
      type="error"
      :title="translate('lblDataErrorTitle')"
      :subtitle="translate('lblDataErrorSubTitle')"
      :content="translate('lblDataErrorText')"
      @close="error = false"
    ></notification-modal>

    <loading-indicator v-if="loading"></loading-indicator>
  </div>
</template>

<script>
/* eslint-disable no-unused-expressions */
import NotificationModal from '../../shared/components/NotificationModal.vue';
import SortOrder from './sort-order.vue';
import FacetFilter from './facet-filter.vue';
import LoadingMixin from '../../shared/mixins/LoadingMixin';
import ResultViewItem from './result-view-item.vue';
import CompareButton from './compare-button.vue';
import { load, store } from '../../shared/State';

export default {
  name: 'ResultView',
  emits: ['change-region-output'],
  data: () => ({
    sortOptions: [],
    facetFilterProperties: null,
    userInput: {},
    resultList: [],
    facetFilterOpenOnMobile: false,
    error: false,
    preLoaded: false,
    comparisonDataKey: 'comparison-ids',
    idsToBeCompared: [],
    sortOrderMandatory: false,
    numberOfItemsShown: 0,
  }),
  props: {
    defaultSettings: {
      type: Object,
      required: true,
    },
    activateComparison: Boolean,
    activateBookingLink: Boolean,
    activateSalesforceFormLink: Boolean,
    activateSubheadline: Boolean,
    comparisonView: String,
    bookingLink: String,
    salesforceFormLink: String,
    comparisonSelectionLabel: String,
    openComparisonLabel: String,
    comparisonViewLink: String,
    maxNrOfVarietiesToBeCompared: {
      type: Number,
      required: false,
    },
    hintVisibleDuration: Number,
    apiUrl: {
      type: String,
      required: true,
    },
    tenant: {
      type: String,
      required: true,
    },
    countryCode: {
      type: String,
      required: true,
    },
    language: {
      type: String,
      required: true,
    },
    crop: {
      type: String,
      required: true,
    },
    postalCode: {
      type: String,
      required: true,
    },
    preloadResults: {
      type: Boolean,
      default: true,
    },
    limitNumberOfResults: Boolean,
    numberOfResultsInitiallyShown: Number,
    benchmarkingToolLink: Object,
  },
  computed: {
    slicedResultList() {
      if (this.numberOfItemsShown) {
        return this.resultList.slice(0, this.numberOfItemsShown);
      }
      return this.resultList;
    },
    moreResultsAvailable() {
      return this.resultList?.length && this.numberOfItemsShown < this.resultList.length;
    },
    comparisonPossible() {
      return this.activateComparison && this.maxNrOfVarietiesToBeCompared > this.nrOfSelectedVarieties;
    },
    nrOfSelectedVarieties() {
      return this.idsToBeCompared.length;
    },
    filterSettingsKey() {
      return `filter-settings-${this.crop}`;
    },
    isFacetFilterDisplayable() {
      return this.facetFilterProperties && Object.keys(this.facetFilterProperties).length;
    },
    isSortOptionsDisplayable() {
      return this.sortOptions && this.sortOptions.length;
    },
  },
  methods: {
    showMoreResults() {
      this.numberOfItemsShown = this.resultList.length;
    },
    resetSortOrder() {
      this.userInput.sortId = this.defaultSettings.sortId;
    },
    filterResults(userInput) {
      if (this.limitNumberOfResults) { this.numberOfItemsShown = this.numberOfResultsInitiallyShown; }
      this.userInput = { ...this.userInput, ...userInput };
      store(this.filterSettingsKey, this.userInput);
      this.loadData();
    },
    reorderResults(order) {
      this.userInput.sortId = order;
      store(this.filterSettingsKey, this.userInput);
      this.loadData();
    },
    async loadUsageId() {
      const query = `query ($tenant: String!, $language: String!, $crop: String!) {
        advisorForm(tenant: $tenant, language: $language, crop: $crop) {
          disableLocation
          sortIdPreselected
          sortIdMandatory
          usages {
            entries {
              id
            }
          }
        }
      }`;
      this.startRequest();
      const result = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          query,
          variables: {
            tenant: this.tenant,
            language: this.language,
            crop: this.crop,
          },
        }),
      })
        .then((response) => response.json())
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.finishRequest();
        });

      this.error = !!result?.data?.errors;

      const returnValue = {
        disableLocation: result.data?.advisorForm?.disableLocation,
      };
      if (result.data?.advisorForm?.usages?.entries?.length) {
        returnValue.usageId = result.data.advisorForm.usages.entries[0].id;
      }
      if (result.data?.advisorForm?.sortIdPreselected) {
        returnValue.sortId = result.data.advisorForm.sortIdPreselected;
      }
      if (result.data?.advisorForm?.sortIdMandatory) {
        returnValue.sortIdMandatory = result.data.advisorForm.sortIdMandatory;
      }

      return returnValue;
    },
    async loadInitially() {
      await this.loadData();
      this.preLoaded = true;
      this.$emit('preload-completed');
    },
    async loadData() {
      const query = `query(
        $tenant: String!,
        $language: String!,
        $crop: String!,
        $countryCode: String,
        $postalCode: String,
        $placeName: String,
        $userInput: AdvisorUserInput) {
        advisorForm(
          tenant: $tenant,
          language: $language,
          crop: $crop,
          countryCode: $countryCode,
          postalCode: $postalCode,
          placeName: $placeName,
          userInput: $userInput) {
          includesFilterCount
          sortIdMandatory
          sortIdPreselected
          usages {
            name
            required
            multipleSelect
            entries {
              id
              name
              numProducts
            }
          }
          properties {
            id
            name
            required
            multipleSelect
            entries {
              id
              name
              usages
              numProducts
            }
          }
          sortings {
            id
            name
            usages
            numProducts
          }
          region {
            regionName
            industryName
          }
        }
        advisorResult(
          tenant: $tenant,
          language: $language,
          crop: $crop,
          countryCode: $countryCode,
          postalCode: $postalCode,
          placeName: $placeName,
          userInput: $userInput) {
          id
          variety
          new
          slogan
          subHeadline
          mainCharacteristics
          url
          shopUrl
          mainImageUrls
          promoted
          promotionText
        }
      }`;
      this.startRequest();
      const result = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          query,
          variables: {
            tenant: this.tenant,
            language: this.language,
            crop: this.crop,
            countryCode: this.countryCode,
            postalCode: this.postalCode,
            userInput: this.userInput,
          },
        }),
      })
        .then((response) => response.json())
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.finishRequest();
        });

      this.error = !!result?.data?.errors;

      if (result?.data?.advisorResult) {
        this.resultList = this.enrichResultWithComparisonData(result?.data?.advisorResult);
      }
      // the api provides sortOptions with the information how many products would show up after selection
      this.sortOptions = result?.data?.advisorForm?.sortings?.filter((o) => o.numProducts > 0 || o.numProducts === null);
      this.sortOrderMandatory = result?.data?.advisorForm?.sortIdMandatory;
      const middleWareSortIdPreselected = result?.data?.advisorForm?.sortIdPreselected;

      // check whether the currently selected sort option would return results
      const defaultOrderIsAllowedAndSelected = () => !this.sortOrderMandatory && this.userInput.sortId === '';
      const selectedOrderIsValid = () => this.sortOptions?.some((option) => option.id === this.userInput.sortId);
      if (!defaultOrderIsAllowedAndSelected() && !selectedOrderIsValid()) {
        this.resetSortOrder();
      }

      if (middleWareSortIdPreselected) {
        if (
          (!this.userInput.sortId && this.sortOrderMandatory) // no falsy value allowed
          || (typeof this.userInput.sortId === 'undefined') // falsy value allowed, but might never have been set
        ) {
          this.userInput.sortId = middleWareSortIdPreselected;
        }
        if (
          (!this.defaultSettings.sortId && this.sortOrderMandatory) // no falsy value allowed
          || (typeof this.defaultSettings.sortId === 'undefined') // falsy value allowed, but might never have been set
        ) {
          // eslint-disable-next-line
          this.defaultSettings.sortId = middleWareSortIdPreselected;
        }
      }

      if (result?.data?.advisorForm) {
        this.facetFilterProperties = (
          ({
            usages,
            properties,
          }) => ({
            // usages are mandatory (can not be deselected)
            usages: !usages ? null : {
              name: usages.name,
              required: usages.required,
              entries: usages.entries.map((entry) => ({
                ...entry,
                mandatory: true,
              })),
            },
            properties,
          })
        )(result.data.advisorForm);

        let regionOutputTmp = '';

        if (result?.data?.advisorForm.region?.regionName) {
          regionOutputTmp = result.data.advisorForm.region.regionName;
          if (result.data.advisorForm.region.industryName) {
            regionOutputTmp = `${regionOutputTmp} (${result.data.advisorForm.region.industryName})`;
          }
        }
        this.$emit('change-region-output', regionOutputTmp);
      }
    },
    reAssignCheckedStates(newData) {
      if (this.facetFilterProperties !== null) {
        // check all facet filter properties that have been checked before

        newData.properties?.forEach((property) => {
          property.entries?.forEach((entry) => {
            entry.checked = this.facetFilterProperties
              ?.properties?.find((prop) => prop.id === property.id)
              ?.entries?.find((e) => e.id === entry.id)?.checked;
          });
        });

        newData.usages?.entries?.forEach((entry) => {
          entry.checked = this.facetFilterProperties
            ?.usages?.entries?.find((e) => e.id === entry.id)?.checked;
        });
      }
      return newData;
    },
    enrichResultWithComparisonData(result) {
      if (this.activateComparison && this.idsToBeCompared) {
        result.forEach((item) => {
          item.toBeCompared = this.idsToBeCompared.includes(item.id);
        });
      }
      return result;
    },
    readComparisonData() {
      this.idsToBeCompared = load(`${this.comparisonDataKey}-${this.crop}`) || [];
    },
    toggleComparison(variety) {
      if (this.activateComparison) {
        if (this.idsToBeCompared.includes(variety.id) && !variety.add) {
          this.idsToBeCompared = this.idsToBeCompared.filter((id) => id !== variety.id);
        }
        if (!this.idsToBeCompared.includes(variety.id) && variety.add) {
          this.idsToBeCompared.push(variety.id);
        }
        this.resultList.find((item) => item.id === variety.id).toBeCompared = variety.add;
        store(`${this.comparisonDataKey}-${this.crop}`, this.idsToBeCompared);
      }
    },
  },
  watch: {
    postalCode: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    preloadResults: {
      handler(preloadActivated) {
        if (!this.preLoaded && preloadActivated) {
          this.loadInitially();
        }
      },
    },
  },
  async created() {
    this.readComparisonData();
    this.userInput = load(this.filterSettingsKey) || { ...this.defaultSettings };
    const {
      usageId,
      disableLocation,
      sortId,
      sortIdMandatory,
    } = await this.loadUsageId();
    if ((!this.userInput.usageId || !this.defaultSettings.usageId) && usageId) {
      // if no usageId is provided by CMS or stored settings than we have to ask the api which ids
      // are available because a usageId must be provided for valid requests.
      if (!this.userInput.usageId) {
        this.userInput.usageId = usageId;
      }
      if (!this.defaultSettings.usageId) {
        // eslint-disable-next-line
        this.defaultSettings.usageId = usageId;
      }
    }
    if (sortIdMandatory) {
      this.sortOrderMandatory = sortIdMandatory;
    }
    const defaultOrderIsNotAllowedButSelected = () => this.sortOrderMandatory && this.userInput.sortId === '';
    if (sortId) {
      if (defaultOrderIsNotAllowedButSelected || typeof this.userInput.sortId === 'undefined') {
        this.userInput.sortId = sortId;
      }
      if (!this.defaultSettings.sortId) {
        // eslint-disable-next-line
        this.defaultSettings.sortId = sortId;
      }
    }
    if (disableLocation) {
      this.$emit('disable-location');
    }
    if (this.preloadResults) {
      await this.loadInitially();
    }
    if (this.limitNumberOfResults) { this.numberOfItemsShown = this.numberOfResultsInitiallyShown; }
  },
  mixins: [LoadingMixin],
  components: {
    CompareButton,
    ResultViewItem,
    SortOrder,
    FacetFilter,
    NotificationModal,
  },
};
</script>

<style lang="scss" scoped>
.result-view {
  @each $breakpoint in (m, l, xl) {
    @include media-breakpoint-up($breakpoint) {
      $gutter: map-get($grid-gutter-widths, $breakpoint);
      width: calc(100% + #{(rem($gutter) * 1)});
      margin-block: 0;
      margin-inline: (rem($gutter) / -2) (rem($gutter) / -1);
    }
  }

  .benchmarking-tool-link,
  .benchmarking-tool-link__mobile {
    p {
      margin: rem(8px) 0;
    }
    img {
      width: 100%;
      height: auto;
    }
  }

  .benchmarking-tool-link {
    display: none;
  }

  .benchmarking-tool-link__mobile {
    margin-top: rem(40px);
  }
}

@include media-breakpoint-up(m) {
  button.facet-filter-opener {
    display: none;
  }
  .result-view {
    display: flex;
    align-items: flex-start;

    /* ::v-deep .facet-filter {
      flex: 0 0 33.33333%;
      max-width: 33.33333%;
      flex-grow: 1;
    } */
    .varieties-menu {
      flex: 0 0 33.33333%;
      max-width: 33.33333%;
      flex-grow: 1;

      @each $breakpoint in (m, l, xl) {
        @include media-breakpoint-up($breakpoint) {
          $gutter: map-get($grid-gutter-widths, $breakpoint);
          padding: 0 (rem($gutter) / 2);
        }
      }
    }

    .results {
      width: 100%;
    }

    .varieties {
      display: flex;
      flex-wrap: wrap;

      &::after {
        content: "";
        flex: auto;
      }
    }

    .load-more {
      padding-top: rem(18px);
      text-align: center;
    }

    .benchmarking-tool-link {
      display: flex;
      flex-direction: column;
      &__mobile {
        display: none;
      }
    }
  }
}
</style>
